/**
 * Комментарии к объекту в панели свойств: список привязанных к нему и кнопка добавить ещё.
 *
 * К одному объекту можно привязать сколько угодно комментариев. Клик по строке выделяет
 * комментарий на плане. Новый комментарий ставит холст (`editor.commentFor`): выноска
 * рядом с объектом, стрелка на него, сразу окошко для текста; несколько выносок встают
 * лесенкой. Комментарии живут на плане, поэтому блок есть только там, где виден чертёж.
 */
import { getCatalogItem } from "@/shared/catalog";
import { findFixture, findFloorItem, type CommentTarget, type PlanLayers } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { SelectionKind, ViewMode, useEditorStore } from "@/widgets/editor";

/** Как назвать объект комментария: имя из каталога, «Дверь», «Стена»… */
export const commentTargetLabel = (layers: PlanLayers, target: CommentTarget): string => {
	switch (target.kind) {
		case "item": {
			const found = findFloorItem(layers, target.id);
			return (found && getCatalogItem(found.item.ref)?.name) || "Предмет";
		}
		case "light": {
			const light = layers.electrical.items[target.id];
			return (light && getCatalogItem(light.ref)?.name) || "Светильник";
		}
		case "fixture": {
			const found = findFixture(layers, target.id);
			return (found && getCatalogItem(found.fixture.ref)?.name) || "Объект на стене";
		}
		case "opening":
			return layers.walls.openings[target.id]?.kind === "window" ? "Окно" : "Дверь";
		case "wall":
			return "Стена";
	}
};

interface Props {
	kind: CommentTarget["kind"];
	id: string;
	/** Комментарий, выделенный сейчас, — подсвечен в списке */
	currentId?: string;
}

export const ObjectComments = ({ kind, id, currentId }: Props) => {
	const viewMode = useEditorStore((s) => s.viewMode);
	const setCommentFor = useEditorStore((s) => s.setCommentFor);
	const setSelection = useEditorStore((s) => s.setSelection);
	const items = useProjectStore((s) => s.project.layers.comments.items);
	if (viewMode !== ViewMode.TwoD && viewMode !== ViewMode.Split) return null;
	const comments = Object.values(items).filter(
		(comment) => comment.target?.kind === kind && comment.target.id === id,
	);

	return (
		<div className="mt-1 flex flex-col gap-1.5">
			{comments.length > 0 && (
				<>
					<UIKit.Text className="text-xs font-semibold tracking-label text-ink uppercase">
						Комментарии · {comments.length}
					</UIKit.Text>
					{comments.map((comment) => (
						<button
							key={comment.id}
							type="button"
							className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs leading-snug transition-colors${
								comment.id === currentId
									? " border-accent bg-accent-soft text-ink"
									: " border-border bg-surface text-ink hover:bg-accent-soft"
							}`}
							onClick={() => setSelection({ kind: SelectionKind.Comment, id: comment.id })}
						>
							<div
								className="mt-1 size-2 shrink-0 rounded-full"
								style={{ background: comment.color }}
							/>
							<UIKit.Text as="span" className="line-clamp-2 min-w-0 flex-1 whitespace-pre-line">
								{comment.text.trim() || "Без текста"}
							</UIKit.Text>
						</button>
					))}
				</>
			)}
			<UIKit.Button
				type="button"
				className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-sm"
				onClick={() => setCommentFor({ kind, id })}
			>
				<UIKit.IconComment />
				{comments.length > 0 ? "Добавить ещё" : "Добавить комментарий"}
			</UIKit.Button>
		</div>
	);
};
